import jwtDecode from 'jwt-decode';

import { FORBIDDEN_STATUS_CODE, UNAUTHORIZED_STATUS_CODE } from '../components/components.constant';
import { REACT_APP_AUTH_URL } from '../config';
import { isExpectedStatusCode } from '../utils/shouldErrorBeDisplayed';

export interface JWTPayload extends Partial<Record<string, unknown>> {
  username: string;
  firstName: string;
  lastName: string;
  refreshToken: string;
  otcAccessAllowed: boolean;
  userRole: string;
  exp: number;
  iat: number;
  companyName?: string;
  companyEmail?: string;
  /** Company id of the currently selected company, e.g. "M_70" */
  selectedCompany?: string;
  intelligenceFreeTrialSubscribed?: boolean;
  intelligenceServiceSubscribed?: boolean;
  mgmtServiceSubscribed?: boolean;
  reportingAccessAllowed?: boolean;
  webAccessAllowed?: boolean;
}

export const getWebToken = (): string | null => localStorage.getItem('webToken');
export const setWebToken = (token: string): void => localStorage.setItem('webToken', token);
export const removeWebToken = (): void => localStorage.removeItem('webToken');

export const getNotAllowedFlag = (): string | null => localStorage.getItem('notAllowed');
export const setNotAllowedFlag = (): void => localStorage.setItem('notAllowed', 'true');
export const removeNotAllowedFlag = (): void => localStorage.removeItem('notAllowed');

export const getRefreshToken = (): string | null => {
  const webToken = getWebToken();

  if (webToken) {
    const { refreshToken } = jwtDecode(webToken) as JWTPayload;

    return refreshToken;
  }

  return null;
};

export const redirectToErrorPage = (statusCode: number | undefined): void => {
  if (statusCode) {
    if (isExpectedStatusCode(statusCode)) {
      window.location.href = `/error?statusCode=${statusCode}`;
    } else {
      redirectToLoginPage();
    }
  } else {
    redirectToLoginPage();
  }
};

export const redirectToLoginPage = (): void => {
  if (REACT_APP_AUTH_URL) {
    window.location.replace(REACT_APP_AUTH_URL);
  }
};

/** Redirect to in-app "Not Signed In" page when user has no token */
export const redirectToNotSignedInPage = (): void => {
  window.location.href = `/error?statusCode=${UNAUTHORIZED_STATUS_CODE}`;
};

export const validateToken = (token: string): void => {
  // Default to false if otcAccessAllowed is missing, undefined, or null
  const { otcAccessAllowed = false } = jwtDecode(token) as JWTPayload;

  if (otcAccessAllowed) {
    removeNotAllowedFlag();
    setWebToken(token);
    initializePendo();
  } else {
    // User doesn't have OTC access (or field is missing)
    // Set token so user remains authenticated, set notAllowed flag, and redirect to error page
    setWebToken(token);
    setNotAllowedFlag();
    redirectToErrorPage(FORBIDDEN_STATUS_CODE);
  }
};

export const getUserRole = (): string => {
  const token = getWebToken();

  return token ? (jwtDecode(token) as JWTPayload).userRole : 'unknown';
};

export const getUserDetails = (): { tokenDetails: JWTPayload | null } => {
  const token = getWebToken();

  if (token) {
    const decodedToken = jwtDecode<JWTPayload>(token);

    return {
      tokenDetails: decodedToken,
    };
  }

  return {
    tokenDetails: null,
  };
};

/** Drop null/undefined/empty values so Pendo never receives blank metadata */
const clean = (obj: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== null && value !== undefined && value !== ''));

/** Subscription flags available on the token, keyed to match the cddata.co.uk Pendo metadata */
const getSubscriptions = (tokenDetails: JWTPayload): Record<string, unknown> =>
  clean({
    cddata_management_service_sub: tokenDetails.mgmtServiceSubscribed,
    cddata_intelligence_service_sub: tokenDetails.intelligenceServiceSubscribed,
    cddata_intelligence_free_trial_sub: tokenDetails.intelligenceFreeTrialSubscribed,
    cddata_otc_sub: tokenDetails.otcAccessAllowed,
    cddata_reporting_sub: tokenDetails.reportingAccessAllowed,
    cddata_web_access_sub: tokenDetails.webAccessAllowed,
  });

// Pendo initialization function
const initializePendo = (): void => {
  const { tokenDetails } = getUserDetails();

  if (window.pendo && tokenDetails) {
    const { username, firstName, lastName, userRole, companyName, selectedCompany } = tokenDetails;
    const userName = username?.toLowerCase();

    window.pendo.initialize({
      visitor: clean({
        id: userName,
        email: userName,
        product_name: 'guidetootc.cddata.co.uk',
        full_name: [firstName, lastName].filter(Boolean).join(' '),
        job_title: userRole,
        login_type: 'database',
        is_trail_user: false,
      }),
      account: clean({
        id: selectedCompany,
        name: companyName,
        subscriptions: getSubscriptions(tokenDetails),
      }),
    });
  }
};
